import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { Plus, Trash2, Eye, EyeOff, Sparkles, Search, X, ChevronDown, ChevronUp, Check, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/currency";

const VIS_FILTERS = [
  { value: "all", label: "all" },
  { value: "visible", label: "visible on shop" },
  { value: "hidden", label: "hidden from shop" },
];

function visibilityBadge(item) {
  if (item.visibility_reason === "force_published") {
    return { Icon: Sparkles, color: "text-brass border-brass", label: "force published" };
  }
  if (item.visibility_reason === "force_hidden") {
    return { Icon: EyeOff, color: "text-red-700 border-red-300", label: "force hidden" };
  }
  return item.is_visible_on_shop
    ? { Icon: Eye, color: "text-brass border-brass/60", label: "auto · visible" }
    : { Icon: EyeOff, color: "text-taupe border-taupe/50", label: "auto · hidden" };
}

function daysInStageBadge(days) {
  if (days === null || days === undefined) return null;
  if (days >= 30) return { tone: "text-red-800 border-red-300 bg-red-50", label: `${days}d in stage`, icon: AlertTriangle };
  if (days >= 14) return { tone: "text-amber-800 border-amber-300 bg-amber-50", label: `${days}d in stage`, icon: AlertTriangle };
  return { tone: "text-taupe border-taupe/40 bg-linen/50", label: `${days}d in stage`, icon: null };
}

/** Multi-token AND match across the most-searchable fields. */
function matchesSearch(item, tokens) {
  if (!tokens.length) return true;
  const haystack = [
    item.title, item.sku, item.brand, item.type, item.color, item.style,
    item.fabric, item.collection, item.description, item.stage,
  ].filter(Boolean).map((v) => String(v).toLowerCase()).join(" ");
  return tokens.every((t) => haystack.includes(t));
}

function topValues(items, field, limit = 8) {
  const counts = new Map();
  for (const it of items) {
    const v = (it[field] || "").trim();
    if (!v) continue;
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, limit).map(([v]) => v);
}

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [q, setQ] = useState("");
  const [visFilter, setVisFilter] = useState("all");
  const [stage, setStage] = useState("");
  const [brand, setBrand] = useState("");
  const [type, setType] = useState("");
  // Archive mode toggles between "active" (everything except Discontinued — default),
  // "archived" (only Discontinued), and "all" (everything including Discontinued).
  const [archiveMode, setArchiveMode] = useState("active");
  const [stages, setStages] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showSourcingTrip, setShowSourcingTrip] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [inv, st] = await Promise.all([
        api.get("/inventory").then((r) => r.data),
        api.get("/pipeline/stages").then((r) => r.data).catch(() => ({ stages: [] })),
      ]);
      setItems(inv);
      setStages(st.stages || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("inventory load failed", e);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const brands = useMemo(() => topValues(items, "brand"), [items]);
  const types = useMemo(() => topValues(items, "type"), [items]);

  const createItem = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/inventory", { title });
      toast.success("piece created.");
      nav(`/admin/inventory/${data.id}`);
    } catch (e2) {
      toast.error("failed to create piece.");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("remove this piece?")) return;
    await api.delete(`/inventory/${id}`);
    toast.success("piece removed.");
    load();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = (ids) => setSelected(new Set(ids));
  const clearSelection = () => setSelected(new Set());

  const bulkPublish = async (mode) => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/inventory/bulk-publish", { item_ids: Array.from(selected), mode });
      toast.success(`${data.updated} piece${data.updated === 1 ? "" : "s"} set to ${mode.replace("_", " ")}.`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "bulk action failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetNiftyManaged = async (managed) => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/inventory/bulk-set-nifty-managed", { item_ids: Array.from(selected), managed });
      toast.success(`${data.updated} piece${data.updated === 1 ? "" : "s"} ${managed ? "marked nifty-managed" : "unmarked nifty-managed"}.`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "bulk nifty toggle failed.");
    } finally {
      setBulkBusy(false);
    }
  };

  // Export the current "Ready to List" pool (Listing & Marketing stage, not sold,
  // not force-hidden) as a Nifty-compatible CSV. Triggers a browser download.
  // When `markManaged` is true, every exported piece is also flipped to
  // managed_by_nifty=True on the server so Maison Pilet's own eBay cross-post
  // will skip them from here on.
  const [exportingNifty, setExportingNifty] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportNifty = async ({ markManaged = false } = {}) => {
    setExportingNifty(true);
    setExportMenuOpen(false);
    try {
      const res = await api.get(`/inventory/export/nifty${markManaged ? "?mark_managed=true" : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(disp);
      a.download = match ? match[1] : `maison-pilet-nifty-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(markManaged ? "csv exported · pieces marked nifty-managed." : "nifty csv exported.");
      if (markManaged) await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "export failed.");
    } finally {
      setExportingNifty(false);
    }
  };

  const visibleCount = useMemo(() => items.filter((i) => i.is_visible_on_shop).length, [items]);
  const tokens = useMemo(() => q.trim().toLowerCase().split(/\s+/).filter(Boolean), [q]);

  const archivedCount = useMemo(() => items.filter((i) => i.stage === "Discontinued").length, [items]);

  const filtered = useMemo(() => items.filter((it) => {
    // Archive mode is the top-level partition: respect it before any other filter.
    if (archiveMode === "active" && it.stage === "Discontinued") return false;
    if (archiveMode === "archived" && it.stage !== "Discontinued") return false;
    if (visFilter === "visible" && !it.is_visible_on_shop) return false;
    if (visFilter === "hidden" && it.is_visible_on_shop) return false;
    if (stage && it.stage !== stage) return false;
    if (brand && it.brand !== brand) return false;
    if (type && it.type !== type) return false;
    return matchesSearch(it, tokens);
  }), [items, tokens, archiveMode, visFilter, stage, brand, type]);

  const activeFilters = [
    archiveMode !== "active" && { label: archiveMode === "archived" ? "archived only" : "incl. archived", clear: () => setArchiveMode("active") },
    visFilter !== "all" && { label: visFilter === "visible" ? "visible on shop" : "hidden from shop", clear: () => setVisFilter("all") },
    stage && { label: `stage · ${stage.toLowerCase()}`, clear: () => setStage("") },
    brand && { label: `brand · ${brand}`, clear: () => setBrand("") },
    type && { label: `type · ${type}`, clear: () => setType("") },
  ].filter(Boolean);

  const clearAll = () => { setQ(""); setVisFilter("all"); setStage(""); setBrand(""); setType(""); setArchiveMode("active"); };

  return (
    <AdminLayout>
      <div data-testid="admin-inventory">
        {/* Header + counter */}
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="editorial-label">atelier · inventory</span>
            <h1 className="font-serif text-5xl lowercase text-charcoal mt-3">every piece, its story.</h1>
          </div>
          <div className="text-right flex items-center gap-6" data-testid="visibility-counter">
            <div className="relative">
              <button
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={exportingNifty}
                className="flex items-center gap-2 border border-taupe/60 text-charcoal hover:bg-charcoal hover:text-ivory hover:border-charcoal px-4 py-2 text-xs uppercase tracking-widest transition-colors disabled:opacity-50"
                data-testid="export-nifty-btn"
                title="download a csv of the pieces currently at listing &amp; marketing formatted for nifty cross-lister"
              >
                <Download className="w-3 h-3" />
                {exportingNifty ? "exporting…" : "export for nifty"}
                <ChevronDown className="w-3 h-3" />
              </button>
              {exportMenuOpen && !exportingNifty && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setExportMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 bg-ivory border border-taupe/60 shadow-xl z-20 w-72 py-2" data-testid="export-nifty-menu">
                    <button
                      onClick={() => exportNifty({ markManaged: false })}
                      className="w-full text-left px-4 py-3 hover:bg-linen"
                      data-testid="export-nifty-plain"
                    >
                      <p className="text-sm text-charcoal">just export csv</p>
                      <p className="text-xs text-espresso mt-1">pieces stay open on both maison pilet + nifty.</p>
                    </button>
                    <button
                      onClick={() => exportNifty({ markManaged: true })}
                      className="w-full text-left px-4 py-3 hover:bg-linen border-t border-taupe/30"
                      data-testid="export-nifty-managed"
                    >
                      <p className="text-sm text-charcoal">export + mark as nifty-managed</p>
                      <p className="text-xs text-espresso mt-1">flag every exported piece so maison pilet skips them on ebay cross-post (no duplicate listings).</p>
                    </button>
                  </div>
                </>
              )}
            </div>
            <button
              onClick={() => setShowSourcingTrip(true)}
              className="flex items-center gap-2 border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-xs uppercase tracking-widest transition-colors"
              data-testid="open-sourcing-trip"
            >
              log sourcing trip
            </button>
            <div>
              <p className="editorial-label">visible on shop</p>
              <p className="font-serif text-3xl text-brass mt-1">
                <span data-testid="visible-count">{visibleCount}</span>
                <span className="text-taupe"> / {items.length}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Search + filters — primary surface */}
        <div className="bg-linen border border-taupe/50 p-5 mb-6" data-testid="inventory-filters">
          {/* Active · Archived · All — top-level partition of inventory. Discontinued pieces
              are hidden under "active" so the daily view stays clean. */}
          <div className="flex flex-wrap gap-2 mb-4" data-testid="archive-tabs">
            {[
              { value: "active", label: "active" },
              { value: "archived", label: `archived${archivedCount ? ` · ${archivedCount}` : ""}` },
              { value: "everything", label: "everything" },
            ].map((t) => {
              const on = archiveMode === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setArchiveMode(t.value)}
                  data-testid={`archive-tab-${t.value}`}
                  className={`text-[0.7rem] tracking-widest uppercase px-4 py-2 border transition-colors ${on ? "bg-charcoal text-ivory border-charcoal" : "bg-transparent text-charcoal border-taupe/60 hover:border-brass hover:text-brass"}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-taupe absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="inventory-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="search title, sku, brand, type, color, fabric, description…"
                className="w-full bg-ivory border border-taupe/40 pl-10 pr-4 py-3 text-charcoal focus:outline-none focus:border-brass"
              />
              {q && (
                <button onClick={() => setQ("")} aria-label="clear" className="absolute right-2 top-1/2 -translate-y-1/2 text-taupe hover:text-charcoal p-1">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <select data-testid="visibility-filter" value={visFilter} onChange={(e) => setVisFilter(e.target.value)} className="bg-ivory border border-taupe/40 px-3 py-3 text-charcoal text-xs tracking-widest uppercase focus:outline-none focus:border-brass">
                {VIS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <select data-testid="stage-filter" value={stage} onChange={(e) => setStage(e.target.value)} className="bg-ivory border border-taupe/40 px-3 py-3 text-charcoal text-xs tracking-widest uppercase focus:outline-none focus:border-brass">
                <option value="">all stages</option>
                {stages.map((s) => <option key={s} value={s}>{s.toLowerCase()}</option>)}
              </select>
              {brands.length > 0 && (
                <select data-testid="brand-filter" value={brand} onChange={(e) => setBrand(e.target.value)} className="bg-ivory border border-taupe/40 px-3 py-3 text-charcoal text-xs tracking-widest uppercase focus:outline-none focus:border-brass">
                  <option value="">all brands</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              )}
              {types.length > 0 && (
                <select data-testid="type-filter" value={type} onChange={(e) => setType(e.target.value)} className="bg-ivory border border-taupe/40 px-3 py-3 text-charcoal text-xs tracking-widest uppercase focus:outline-none focus:border-brass">
                  <option value="">all types</option>
                  {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              )}
            </div>
          </div>

          {(activeFilters.length > 0 || q) && (
            <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-taupe/30" data-testid="active-filters">
              <span className="editorial-label">active:</span>
              {q && (
                <span className="text-xs bg-ivory border border-taupe/40 px-2 py-1 flex items-center gap-1.5">
                  search · &quot;{q}&quot;
                  <button onClick={() => setQ("")}><X className="w-3 h-3" /></button>
                </span>
              )}
              {activeFilters.map((f) => (
                <span key={f.label} className="text-xs bg-ivory border border-taupe/40 px-2 py-1 flex items-center gap-1.5">
                  {f.label}
                  <button onClick={f.clear}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <button onClick={clearAll} className="ml-auto text-xs editorial-label text-brass hover:text-espresso" data-testid="clear-all-filters">clear all</button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-taupe editorial-label" data-testid="filter-result-count">
            showing {filtered.length} of {items.length} {tokens.length || activeFilters.length ? "(filtered)" : ""}
          </p>
          <button
            onClick={() => setShowCreate((s) => !s)}
            data-testid="toggle-create-form"
            className="text-xs editorial-label text-brass hover:text-espresso flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> {showCreate ? "hide" : "add a new piece"}
            {showCreate ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
        </div>

        {showCreate && (
          <form onSubmit={createItem} className="bg-ivory border border-brass/40 p-5 mb-6 flex flex-col sm:flex-row gap-4 items-end" data-testid="new-item-form">
            <div className="flex-1 w-full">
              <label className="editorial-label">new piece title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. olive trench coat" autoFocus className="w-full bg-transparent border-b border-taupe py-2 mt-2 focus:outline-none focus:border-brass text-charcoal" data-testid="new-item-title" />
            </div>
            <button type="submit" disabled={creating || !title.trim()} data-testid="new-item-submit" className="bg-brass hover:bg-espresso text-ivory px-6 py-3 text-xs tracking-[0.25em] uppercase transition-colors disabled:opacity-50 flex items-center gap-2">
              <Plus className="w-4 h-4" /> {creating ? "creating…" : "create"}
            </button>
          </form>
        )}

        <div className="overflow-x-auto border border-taupe/40">
          {selected.size > 0 && (
            <div className="bg-brass/10 border-b border-brass/40 px-5 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-action-bar">
              <span className="editorial-label text-brass">
                {selected.size} selected
              </span>
              <button onClick={() => bulkPublish("force_published")} disabled={bulkBusy} data-testid="bulk-force-publish" className="text-xs tracking-[0.2em] uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> force publish
              </button>
              <button onClick={() => bulkPublish("force_hidden")} disabled={bulkBusy} data-testid="bulk-force-hidden" className="text-xs tracking-[0.2em] uppercase border border-red-700 text-red-700 hover:bg-red-700 hover:text-ivory px-3 py-1.5 disabled:opacity-50 flex items-center gap-1">
                <EyeOff className="w-3 h-3" /> force hidden
              </button>
              <button onClick={() => bulkPublish("auto")} disabled={bulkBusy} data-testid="bulk-auto" className="text-xs tracking-[0.2em] uppercase border border-taupe text-charcoal hover:border-brass hover:text-brass px-3 py-1.5 disabled:opacity-50">
                back to auto
              </button>
              <span className="text-taupe">·</span>
              <button onClick={() => bulkSetNiftyManaged(true)} disabled={bulkBusy} data-testid="bulk-nifty-managed" className="text-xs tracking-[0.2em] uppercase border border-brass text-brass hover:bg-brass hover:text-ivory px-3 py-1.5 disabled:opacity-50 flex items-center gap-1" title="skip maison pilet's ebay cross-post for these pieces — nifty owns them">
                ★ nifty-managed
              </button>
              <button onClick={() => bulkSetNiftyManaged(false)} disabled={bulkBusy} data-testid="bulk-nifty-unmanaged" className="text-xs tracking-[0.2em] uppercase border border-taupe text-charcoal hover:border-brass hover:text-brass px-3 py-1.5 disabled:opacity-50" title="return these pieces to maison pilet's own cross-post">
                un-nifty
              </button>
              <button onClick={clearSelection} className="ml-auto text-xs editorial-label text-taupe hover:text-charcoal" data-testid="bulk-clear">
                clear selection
              </button>
            </div>
          )}
          <table className="w-full text-sm">
            <thead className="bg-linen">
              <tr className="text-left">
                <th className="editorial-label px-3 py-4 border-b border-taupe/50 w-10">
                  <input
                    type="checkbox"
                    aria-label="select all visible"
                    data-testid="select-all"
                    onChange={(e) => e.target.checked ? selectAllFiltered(filtered.map((i) => i.id)) : clearSelection()}
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    className="accent-brass"
                  />
                </th>
                {["piece", "sku", "brand", "type", "stage", "shop", "sale price", ""].map((h) => (
                  <th key={h} className="editorial-label px-5 py-4 border-b border-taupe/50">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((it, idx) => {
                const v = visibilityBadge(it);
                const dBadge = daysInStageBadge(it.days_in_stage);
                const isSelected = selected.has(it.id);
                return (
                  <tr key={it.id} className={`border-b border-taupe/30 hover:bg-linen/50 ${isSelected ? "bg-brass/5" : ""}`} data-testid={`row-${idx}`}>
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(it.id)}
                        data-testid={`select-${idx}`}
                        aria-label={`select ${it.title}`}
                        className="accent-brass"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <Link to={`/admin/inventory/${it.id}`} className="flex items-center gap-4 group">
                        <div className="w-12 h-12 bg-linen overflow-hidden">
                          {it.photos?.[0] ? <img src={fileUrl(it.photos[0])} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                        <span className="font-serif text-lg lowercase text-charcoal group-hover:text-brass">{it.title}</span>
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-charcoal text-xs tracking-widest uppercase">{it.sku || "—"}</td>
                    <td className="px-5 py-4 text-charcoal">{it.brand || "—"}</td>
                    <td className="px-5 py-4 text-charcoal">{it.type || "—"}</td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="inline-block text-[0.65rem] tracking-widest uppercase border border-brass text-brass px-3 py-1 w-fit">{it.stage}</span>
                        {it.ebay_listing_id && (
                          <a
                            href={it.ebay_listing_url || "#"}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[0.6rem] tracking-widest uppercase border border-emerald-700/50 text-emerald-800 bg-emerald-50 px-2 py-0.5 w-fit hover:bg-emerald-100"
                            data-testid={`ebay-badge-${idx}`}
                            title={`eBay #${it.ebay_listing_id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-700" /> on ebay ↗
                          </a>
                        )}
                        {it.managed_by_nifty && (
                          <span
                            data-testid={`nifty-badge-${idx}`}
                            title="nifty owns cross-post for this piece — maison pilet's ebay cross-post is suppressed"
                            className="inline-flex items-center gap-1 text-[0.6rem] tracking-widest uppercase border border-brass/60 text-brass bg-brass/5 px-2 py-0.5 w-fit"
                          >
                            ★ nifty
                          </span>
                        )}
                        {dBadge && (
                          <span data-testid={`days-${idx}`} className={`inline-flex items-center gap-1 text-[0.6rem] tracking-widest uppercase border px-2 py-0.5 w-fit ${dBadge.tone}`}>
                            {dBadge.icon && <dBadge.icon className="w-2.5 h-2.5" />}
                            {dBadge.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span data-testid={`vis-${idx}`} className={`inline-flex items-center gap-1.5 text-[0.6rem] tracking-widest uppercase border px-2 py-1 ${v.color}`}>
                        <v.Icon className="w-3 h-3" /> {v.label}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-charcoal">{formatMoney(it.sale_price)}</td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => remove(it.id)} className="text-taupe hover:text-red-700" data-testid={`delete-${idx}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan="9" className="text-center py-16 font-serif text-2xl lowercase text-taupe">no pieces match — adjust the filters above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {showSourcingTrip && <SourcingTripDialog items={items} onClose={() => setShowSourcingTrip(false)} onSaved={() => { setShowSourcingTrip(false); load(); }} />}
      </div>
    </AdminLayout>
  );
}

// ---------------- Sourcing Trip dialog ----------------
function SourcingTripDialog({ items, onClose, onSaved }) {
  const unsold = useMemo(() => items.filter((i) => !i.sold && !(i.discontinue_reason || "").length).sort((a, b) => (a.title || "").localeCompare(b.title || "")), [items]);
  const [vendor, setVendor] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().slice(0, 10));
  const [total, setTotal] = useState(0);
  const [splitMethod, setSplitMethod] = useState("equal");
  const [receiptPath, setReceiptPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [customAmounts, setCustomAmounts] = useState({});
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  const toggle = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReceiptPath(data.path);
      toast.success("receipt uploaded.");
    } catch { toast.error("upload failed."); } finally { setUploading(false); }
  };

  const selectedItems = unsold.filter((i) => selectedIds.has(i.id));
  // Preview the split locally so the user sees the prorate before saving.
  const preview = useMemo(() => {
    if (selectedItems.length === 0 || !total) return {};
    const out = {};
    if (splitMethod === "equal") {
      const per = Math.round((total / selectedItems.length) * 100) / 100;
      selectedItems.forEach((i) => { out[i.id] = per; });
    } else if (splitMethod === "weight") {
      const weights = selectedItems.map((i) => Number(i.weight || 0));
      const sum = weights.reduce((a, b) => a + b, 0);
      if (sum === 0) {
        const per = Math.round((total / selectedItems.length) * 100) / 100;
        selectedItems.forEach((i) => { out[i.id] = per; });
      } else {
        selectedItems.forEach((i, idx) => {
          out[i.id] = Math.round(((total * weights[idx]) / sum) * 100) / 100;
        });
      }
    } else if (splitMethod === "custom") {
      selectedItems.forEach((i) => { out[i.id] = Number(customAmounts[i.id] || 0); });
    }
    return out;
  }, [selectedItems, total, splitMethod, customAmounts]);

  const customSum = Object.values(preview).reduce((a, b) => a + Number(b || 0), 0);
  const customSumMatches = splitMethod !== "custom" || Math.abs(customSum - total) < 0.01;

  const save = async () => {
    if (!vendor || !purchaseDate || !total || selectedIds.size === 0) {
      toast.error("vendor, date, total, and at least one item required.");
      return;
    }
    if (!customSumMatches) {
      toast.error(`custom amounts must sum to ${total} (currently ${customSum.toFixed(2)}).`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/inventory/sourcing_trip", {
        vendor,
        purchase_date: purchaseDate,
        total_amount: Number(total),
        receipt_path: receiptPath,
        split_method: splitMethod,
        notes,
        items: selectedItems.map((i) => ({ inventory_id: i.id, custom_amount: customAmounts[i.id] ? Number(customAmounts[i.id]) : null })),
      });
      toast.success(`sourcing trip logged · ${selectedIds.size} pieces updated.`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "failed to save sourcing trip.");
    } finally { setBusy(false); }
  };

  const filtered = unsold.filter((i) => !filter || `${i.title} ${i.sku} ${i.brand}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-charcoal/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ivory border border-taupe/50 max-w-4xl w-full max-h-[92vh] overflow-y-auto p-7" onClick={(e) => e.stopPropagation()} data-testid="sourcing-trip-dialog">
        <p className="editorial-label">log sourcing trip</p>
        <h3 className="font-serif text-3xl lowercase text-charcoal mb-6">split one receipt across many pieces.</h3>

        <div className="grid md:grid-cols-3 gap-5 mb-5">
          <label className="flex flex-col">
            <span className="editorial-label mb-1">vendor</span>
            <input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Goodwill Marietta" className="border border-taupe/40 px-3 py-2 bg-white" data-testid="st-vendor" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">purchase date</span>
            <input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="st-date" />
          </label>
          <label className="flex flex-col">
            <span className="editorial-label mb-1">total amount ($)</span>
            <input type="number" step="0.01" value={total} onChange={(e) => setTotal(Number(e.target.value || 0))} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="st-total" />
          </label>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-5">
          <label className="flex flex-col">
            <span className="editorial-label mb-1">split method</span>
            <select value={splitMethod} onChange={(e) => setSplitMethod(e.target.value)} className="border border-taupe/40 px-3 py-2 bg-white" data-testid="st-split-method">
              <option value="equal">equal · total ÷ pieces</option>
              <option value="weight">by weight · proportional to kg</option>
              <option value="custom">custom · enter each amount</option>
            </select>
          </label>
          <label className="flex flex-col md:col-span-2">
            <span className="editorial-label mb-1">receipt</span>
            {receiptPath ? (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-emerald-800">✓ {receiptPath.split("/").pop()}</span>
                <button onClick={() => setReceiptPath(null)} className="text-red-700 underline">remove</button>
              </div>
            ) : (
              <label className="border border-taupe/40 px-3 py-2 bg-white cursor-pointer text-xs uppercase tracking-widest text-center">
                {uploading ? "uploading…" : "upload receipt (image / pdf)"}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="st-receipt" />
              </label>
            )}
          </label>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <p className="editorial-label">select pieces sourced on this trip · {selectedIds.size} selected</p>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter by title / sku / brand" className="border border-taupe/40 px-3 py-1 text-xs bg-white w-72" />
        </div>
        <div className="border border-taupe/30 max-h-72 overflow-y-auto mb-5" data-testid="st-items-list">
          <table className="w-full text-sm">
            <tbody>
              {filtered.length === 0 && <tr><td className="text-center text-taupe py-6">no unsold pieces match.</td></tr>}
              {filtered.map((i) => {
                const sel = selectedIds.has(i.id);
                return (
                  <tr key={i.id} className={`border-b border-taupe/15 ${sel ? "bg-linen/50" : ""}`}>
                    <td className="px-3 py-2 w-10">
                      <input type="checkbox" checked={sel} onChange={() => toggle(i.id)} data-testid={`st-pick-${i.sku}`} />
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{i.sku}</td>
                    <td className="px-3 py-2">{i.title}</td>
                    <td className="px-3 py-2 text-taupe text-xs">{i.brand}</td>
                    <td className="px-3 py-2 text-taupe text-xs">{(i.weight || 0).toFixed(2)} kg</td>
                    <td className="px-3 py-2 text-right font-serif">
                      {sel && splitMethod === "custom" ? (
                        <input type="number" step="0.01" value={customAmounts[i.id] || ""} onChange={(e) => setCustomAmounts({ ...customAmounts, [i.id]: e.target.value })} className="border border-taupe/40 px-2 py-1 w-24 text-right bg-white" data-testid={`st-custom-${i.sku}`} />
                      ) : sel && preview[i.id] !== undefined ? (
                        <span className="text-brass">{formatMoney(preview[i.id])}</span>
                      ) : (
                        <span className="text-taupe">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {splitMethod === "custom" && (
          <p className={`text-xs mb-3 ${customSumMatches ? "text-emerald-800" : "text-red-700"}`}>
            custom sum: {formatMoney(customSum)} {customSumMatches ? "= total ✓" : `≠ ${formatMoney(total)}`}
          </p>
        )}

        <label className="flex flex-col mb-5">
          <span className="editorial-label mb-1">notes (optional)</span>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="border border-taupe/40 px-3 py-2 bg-white" />
        </label>

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 border border-taupe text-xs uppercase tracking-widest">cancel</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-charcoal text-ivory text-xs uppercase tracking-widest disabled:opacity-50" data-testid="st-save">
            {busy ? "saving…" : "save sourcing trip"}
          </button>
        </div>
      </div>
    </div>
  );
}
